import { useState, useEffect, useCallback, useRef } from "react";
import { Bell, BellOff } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const LS_ENABLED = "pb_notif_enabled";
const LS_LAST_NOTIFIED = "pb_notif_last"; // { [itemId]: "YYYY-MM-DD" }

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function readLastNotified() {
  try { return JSON.parse(localStorage.getItem(LS_LAST_NOTIFIED) || "{}"); }
  catch { return {}; }
}

function writeLastNotified(map) {
  try { localStorage.setItem(LS_LAST_NOTIFIED, JSON.stringify(map)); } catch { /* ignore quota */ }
}

/** Bell-toggle in the header. Requests Notification permission and fires local notifications
 * for expired / soon-to-expire pantry items whenever the user opens the app (de-duplicated
 * per item per calendar day via localStorage). */
export default function ExpiryNotifier() {
  const [enabled, setEnabled] = useState(() => localStorage.getItem(LS_ENABLED) === "1");
  const [permission, setPermission] = useState(() =>
    typeof Notification !== "undefined" ? Notification.permission : "unsupported"
  );
  const checkedRef = useRef(false);

  const fireNotifications = useCallback(async () => {
    if (permission !== "granted") return;
    try {
      const res = await api.getExpiringItems();
      const expired = res.data?.expired || [];
      const expiring = res.data?.expiring || [];
      const seen = readLastNotified();
      const today = todayStr();
      const toNotify = [...expired, ...expiring].filter((it) => seen[it.id] !== today);
      if (toNotify.length === 0) return;
      // Group into a single summary notification to avoid spam
      const title = `Varer som utløper (${toNotify.length})`;
      const body = toNotify.slice(0, 4).map((i) => {
        const days = i.days_until_expiry;
        const suffix = days < 0 ? "(utgått)" : days === 0 ? "(i dag)" : `(om ${days}d)`;
        return `${i.name} ${suffix}`;
      }).join("\n") + (toNotify.length > 4 ? `\n…og ${toNotify.length - 4} til` : "");

      const n = new Notification(title, {
        body,
        icon: "/icons/icon-192.png",
        badge: "/icons/icon-192.png",
        tag: "pb-expiry",
        requireInteraction: false,
      });
      n.onclick = () => {
        window.focus();
        window.location.href = "/pantry?filter=expiring";
        n.close();
      };

      // Mark as seen for today
      const next = { ...seen };
      toNotify.forEach((it) => { next[it.id] = today; });
      writeLastNotified(next);
    } catch { /* fail silently */ }
  }, [permission]);

  useEffect(() => {
    if (!enabled || checkedRef.current) return;
    checkedRef.current = true;
    const t = setTimeout(fireNotifications, 2500); // after app mounts
    return () => clearTimeout(t);
  }, [enabled, fireNotifications]);

  if (permission === "unsupported") return null;

  const toggle = async () => {
    if (!enabled) {
      let p = permission;
      if (p === "default") {
        p = await Notification.requestPermission();
        setPermission(p);
      }
      if (p !== "granted") {
        toast.error("Tillat varsler i nettleseren for å motta påminnelser");
        return;
      }
      setEnabled(true);
      localStorage.setItem(LS_ENABLED, "1");
      toast.success("Varsler aktivert – du får beskjed når varer utløper");
      // Fire once immediately
      checkedRef.current = false;
    } else {
      setEnabled(false);
      localStorage.setItem(LS_ENABLED, "0");
      toast.info("Varsler slått av");
    }
  };

  const Icon = enabled && permission === "granted" ? Bell : BellOff;
  return (
    <button
      onClick={toggle}
      className={`p-2 rounded-lg transition-colors ${
        enabled && permission === "granted"
          ? "text-seawater-600 hover:bg-seawater-50"
          : "text-stone-400 hover:bg-stone-100"
      }`}
      title={enabled ? "Utløpsvarsler er på (klikk for å slå av)" : "Slå på varsler for utløpende varer"}
      data-testid="expiry-notifier-toggle"
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}
